// src/pages/admin/services/groupService.js
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';

export const fetchGroups = async (users) => {
  try {
    const db = getFirestore();
    const groupsSnapshot = await getDocs(collection(db, "groups"));
    const groupsData = [];

    for (const groupDoc of groupsSnapshot.docs) {
      const groupData = groupDoc.data();
      const groupId = groupDoc.id;

      // user.groupId is the source of truth, kept in sync by UserManagement.
      const mentors = users.filter(u => u.role === 'mentor' && u.groupId === groupId);
      const learners = users.filter(u => u.role === 'learner' && u.groupId === groupId);

      groupsData.push({
        id: groupId,
        name: groupData.name || "Unnamed Group",
        mentorIds: mentors.map(u => u.id),
        mentors,
        learnerIds: learners.map(u => u.id),
        learners,
        createdAt: groupData.createdAt?.toDate() || new Date()
      });
    }

    return groupsData.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Error fetching groups:", error);
    throw error;
  }
};

export const saveGroup = async (groupData, groupId = null, previousMentorIds = [], previousLearnerIds = []) => {
  try {
    const db = getFirestore();
    const batch = writeBatch(db);
    const { name, mentorIds, learnerIds } = groupData;

    let savedGroupId;
    if (groupId) {
      savedGroupId = groupId;
      batch.update(doc(db, "groups", savedGroupId), { name, mentorIds: mentorIds || [], learnerIds: learnerIds || [], updatedAt: serverTimestamp() });
    } else {
      const groupRef = doc(collection(db, "groups"));
      savedGroupId = groupRef.id;
      batch.set(groupRef, { name, mentorIds: mentorIds || [], learnerIds: learnerIds || [], createdAt: serverTimestamp(), updatedAt: serverTimestamp(), isActive: true });
    }

    for (const mentorId of (mentorIds || [])) {
      if (typeof mentorId === 'string' && mentorId.trim()) {
        const d = await getDoc(doc(db, "users", mentorId));
        if (d.exists() && d.data().groupId !== savedGroupId) batch.update(doc(db, "users", mentorId), { groupId: savedGroupId });
      }
    }
    for (const mentorId of (previousMentorIds || [])) {
      if (!mentorIds?.includes(mentorId) && typeof mentorId === 'string' && mentorId.trim())
        batch.update(doc(db, "users", mentorId), { groupId: null });
    }
    for (const learnerId of (learnerIds || [])) {
      if (typeof learnerId === 'string' && learnerId.trim())
        batch.update(doc(db, "users", learnerId), { groupId: savedGroupId });
    }
    for (const learnerId of (previousLearnerIds || [])) {
      if (!learnerIds?.includes(learnerId) && typeof learnerId === 'string' && learnerId.trim())
        batch.update(doc(db, "users", learnerId), { groupId: null });
    }

    await batch.commit();
    return savedGroupId;
  } catch (error) {
    console.error("Error saving group:", error);
    throw error;
  }
};

export const deleteGroup = async (groupId, mentorIds, learnerIds) => {
  try {
    const db = getFirestore();
    const batch = writeBatch(db);

    batch.delete(doc(db, "groups", groupId));
    for (const id of (Array.isArray(mentorIds) ? mentorIds : [])) batch.update(doc(db, "users", id), { groupId: null });
    for (const id of (Array.isArray(learnerIds) ? learnerIds : [])) batch.update(doc(db, "users", id), { groupId: null });

    const assignmentsSnapshot = await getDocs(collection(db, "assignments"));
    assignmentsSnapshot.forEach(docSnapshot => {
      const data = docSnapshot.data();
      const updates = {};
      if (Array.isArray(data.targetGroups) && data.targetGroups.includes(groupId))
        updates.targetGroups = data.targetGroups.filter(id => id !== groupId);
      if (data.groupSettings?.[groupId]) {
        const { [groupId]: _, ...remaining } = data.groupSettings;
        updates.groupSettings = remaining;
      }
      if (Object.keys(updates).length) batch.update(docSnapshot.ref, updates);
    });

    await batch.commit();
  } catch (error) {
    console.error("Error deleting group:", error);
    throw error;
  }
};